import React, { useState, useEffect, useCallback } from 'react'
import {
  processBatch,
  DEFAULT_OPTIONS,
  type ProcessingOptions,
} from './lib/pipeline'
import {
  recoverSession,
  reprocessInterrupted,
  clearAllSessionData,
} from './lib/session-recovery'
import {
  getStorageEstimate,
  deleteFromOPFS,
  deleteDirectoryFromOPFS,
} from './lib/storage/opfs'
import { deleteFileRecord } from './lib/storage/idb'
import { exportRecordsToZip } from './lib/export-zip'
import type { FileRecord } from './lib/storage/types'

import { HeaderStudio } from './components/studio/HeaderStudio'
import { SessionRecoveryBanner } from './components/studio/SessionRecoveryBanner'
import { DropZoneStudio } from './components/studio/DropZoneStudio'
import { StudioPresetsBar } from './components/studio/StudioPresetsBar'
import { TelemetryGrid } from './components/studio/TelemetryGrid'
import { FileQueueBento } from './components/studio/FileQueueBento'
import { ComparisonModal } from './components/studio/ComparisonModal'

export function App() {
  const [records, setRecords] = useState<FileRecord[]>([])
  const [options, setOptions] = useState<ProcessingOptions>(DEFAULT_OPTIONS)
  const [storageUsage, setStorageUsage] = useState({ usage: 0, quota: 0 })
  const [isProcessing, setIsProcessing] = useState(false)
  const [isExportingZip, setIsExportingZip] = useState(false)
  const [restoredCount, setRestoredCount] = useState(0)
  const [storageWarning, setStorageWarning] = useState(false)
  const [inspectingRecord, setInspectingRecord] = useState<FileRecord | null>(null)

  const handleProgress = useCallback((updated: FileRecord) => {
    setRecords((prev) => {
      const idx = prev.findIndex((r) => r.id === updated.id)
      if (idx === -1) {
        return [updated, ...prev]
      }
      const next = [...prev]
      next[idx] = updated
      return next
    })
  }, [])

  // Initialize session and storage telemetry on mount
  useEffect(() => {
    let mounted = true

    async function initSession() {
      try {
        const est = await getStorageEstimate()
        if (mounted) setStorageUsage(est)

        const recovery = await recoverSession()
        if (!mounted) return

        if (recovery.allFiles.length > 0) {
          setRecords(recovery.allFiles)
          setRestoredCount(recovery.allFiles.length)
          setStorageWarning(recovery.storageWarning)

          // Auto-resume any interrupted pending jobs
          if (recovery.needsReprocessing.length > 0) {
            setIsProcessing(true)
            await reprocessInterrupted(recovery.needsReprocessing, options, (rec) => {
              if (mounted) handleProgress(rec)
            })
            if (mounted) setIsProcessing(false)
          }
        }
      } catch (err) {
        console.error('Error recovering session:', err)
      }
    }

    initSession()

    return () => {
      mounted = false
    }
  }, [handleProgress, options])

  // Ingest and process a batch of files
  const handleFilesSelected = async (files: File[]) => {
    if (files.length === 0) return
    setIsProcessing(true)

    try {
      await processBatch(files, options, (rec) => {
        handleProgress(rec)
      })
    } catch (err) {
      console.error('Error during batch processing:', err)
    } finally {
      setIsProcessing(false)
      const est = await getStorageEstimate()
      setStorageUsage(est)
    }
  }

  // Delete single file record and clean OPFS storage
  const handleDeleteRecord = async (id: string) => {
    const target = records.find((r) => r.id === id)
    if (!target) return

    setRecords((prev) => prev.filter((r) => r.id !== id))

    try {
      await deleteFileRecord(id)
      await deleteFromOPFS(target.opfsPath)
      await deleteDirectoryFromOPFS(`results/${id}`)
      const est = await getStorageEstimate()
      setStorageUsage(est)
    } catch (err) {
      console.error('Error deleting record:', err)
    }
  }

  // Clear all data from OPFS and IndexedDB
  const handleClearSession = async () => {
    if (records.length === 0) return
    const confirmed = window.confirm(
      '¿Deseas purgar todos los archivos y reiniciar el estudio local?',
    )
    if (!confirmed) return

    try {
      await clearAllSessionData(records.map((r) => r.id))
      setRecords([])
      setRestoredCount(0)
      const est = await getStorageEstimate()
      setStorageUsage(est)
    } catch (err) {
      console.error('Error clearing session:', err)
    }
  }

  // Export all completed files as streaming ZIP
  const handleExportZip = async () => {
    setIsExportingZip(true)
    try {
      await exportRecordsToZip(records, 'nitido-optimizados.zip')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al exportar archivo ZIP.')
    } finally {
      setIsExportingZip(false)
    }
  }

  const completedCount = records.filter((r) => r.status === 'done').length

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: '0 20px 80px 20px' }}>
      {/* Bioluminescent Aurora Atmosphere */}
      <div className="aurora-glow-top" />
      <div className="aurora-glow-bottom" />

      {/* Main Studio Container */}
      <main
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '1240px',
          margin: '0 auto',
        }}
      >
        <HeaderStudio
          completedCount={completedCount}
          totalCount={records.length}
          storageUsage={storageUsage}
          isExportingZip={isExportingZip}
          onExportZip={handleExportZip}
          onClearSession={handleClearSession}
        />

        {restoredCount > 0 && (
          <SessionRecoveryBanner
            restoredCount={restoredCount}
            storageWarning={storageWarning}
            onDismiss={() => setRestoredCount(0)}
            onClear={handleClearSession}
          />
        )}

        <DropZoneStudio
          onFilesSelected={handleFilesSelected}
          isProcessing={isProcessing}
        />

        <StudioPresetsBar
          options={options}
          onChangeOptions={setOptions}
        />

        {records.length > 0 && <TelemetryGrid records={records} />}

        <FileQueueBento
          records={records}
          onDeleteRecord={handleDeleteRecord}
          onInspectRecord={(rec) => setInspectingRecord(rec)}
        />

        {inspectingRecord && (
          <ComparisonModal
            record={inspectingRecord}
            onClose={() => setInspectingRecord(null)}
          />
        )}

        {/* Studio Footer */}
        <footer
          style={{
            marginTop: '64px',
            paddingTop: '24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong style={{ color: '#ffffff' }}>Nítido.</strong>
            <span>BEYOND TECH. HUMAN SPORT.</span>
          </div>

          <div>
            Motor WebAssembly + OPFS + Web Workers · 100% In-Browser
          </div>
        </footer>
      </main>
    </div>
  )
}

export default App
